import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { errorMessage } from '../../../../lib/errors';
import toast from 'react-hot-toast';

interface EditCustomerModalProps {
    customer: any;
    onClose: () => void;
    onSuccess: () => void;
}

export default function EditCustomerModal({ customer, onClose, onSuccess }: EditCustomerModalProps) {
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        address: '',
        notes: ''
    });

    useEffect(() => {
        if (customer) {
            setFormData({
                full_name: customer.full_name || '',
                email: customer.email || '',
                phone: customer.phone || '',
                address: customer.address || '',
                notes: customer.notes || ''
            });
        }
    }, [customer]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const { error } = await supabase
                .from('customers')
                .update({
                    full_name: formData.full_name,
                    email: formData.email || null,
                    phone: formData.phone || null,
                    address: formData.address || null,
                    notes: formData.notes || null
                })
                .eq('id', customer.id);

            if (error) throw error;
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Error updating customer:', err);
            toast.error('Error updating customer: ' + errorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in">
            <div className="w-full max-w-md bg-panel border border-line rounded-xl shadow-2xl p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-fg-4 hover:text-fg transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                <h2 className="text-xl font-bold text-fg mb-6">Edit Customer</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Full Name</label>
                        <input
                            type="text"
                            required
                            value={formData.full_name}
                            onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2 text-fg focus:ring-2 focus:ring-accent outline-none"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Phone</label>
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2 text-fg focus:ring-2 focus:ring-accent outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Email</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2 text-fg focus:ring-2 focus:ring-accent outline-none"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Address</label>
                        <textarea
                            value={formData.address}
                            onChange={e => setFormData({ ...formData, address: e.target.value })}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2 text-fg focus:ring-2 focus:ring-accent outline-none h-20 resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Internal Notes</label>
                        <textarea
                            value={formData.notes}
                            onChange={e => setFormData({ ...formData, notes: e.target.value })}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2 text-fg focus:ring-2 focus:ring-accent outline-none h-24 resize-none"
                            placeholder="Add private notes about this client..."
                        />
                    </div>

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full fd-btn-primary font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
